<script setup lang="ts">
import BasePage from '@/components/pages/BasePage/BasePage.vue';
import BasePageTitle from '@/components/pages/BasePage/BasePageTitle.vue';
import PtwCancelButton from '@/components/buttons/PtwCancelButton.vue';
import AccountForm from './components/AccountForm.vue';
import { useVeeForm } from '@/components/forms/useVeeForm';
import { api } from '@/api';
import { routenames } from '@/router/routenames';
import {
  CreateUpdateUserDtoTypeEnum,
  UserDtoTypeEnum,
  type CreateUpdateUserDto,
  type UserDto,
} from '@/api/generator';
import { onMounted } from 'vue';
import { useLoading } from '@/utils/useLoading';
import type { AccountEditViewProps } from './types';
import BasePageStatus from '@/components/pages/BasePage/BasePageStatus.vue';
import { fullName } from '@/utils/helper';
import { useRouter } from 'vue-router';
import { entityNotFoundErrorHandler, formBackendErrorHandler } from '@/errorHandling';
import { useToken } from '@/utils/tokens/tokens';
import VeeSubmitButton from '@/components/forms/SubmitButton/VeeSubmitButton.vue';
import { useFormValues } from 'vee-validate';
import { backendValidator } from '@/validation/validators/backendValidator';
import { useAccountStore } from '@/stores/accountStore';

const router = useRouter();

const props = defineProps<AccountEditViewProps>();

const { form } = useVeeForm<UserDto>({
  schemaName: 'UserDto',
  mode: 'edit',
  customValidateFunctions: [
    backendValidator((userDto, errorHandlers) =>
      api.usersApi.validate(
        {
          id: userDto.id,
          createUpdateUserDto: {
            ...userDto,
            $type:
              CreateUpdateUserDtoTypeEnum.PtwPtw100AppZeroApplicationSharedAuthorizationUsersDtoCreateUpdateUserDtoPtwPtw100AppZeroApplicationShared,
          },
        },
        errorHandlers,
      ),
    ),
  ],
});

const { resetForm, handleSubmit } = form;
const values = useFormValues<UserDto>();

const { action: loadAccount, loading } = useLoading(async () => {
  const { result } = await api.usersApi.getUser(
    {
      id: props.id,
    },
    [entityNotFoundErrorHandler(router)],
  );

  if (result) {
    resetForm({ values: result });
  }
}, true);

const token = useToken({
  id: props.id,
  $type:
    UserDtoTypeEnum.PtwPtw100AppZeroApplicationSharedAuthorizationUsersDtoUserDtoPtwPtw100AppZeroApplicationShared,
});

onMounted(token.request);

token.on.initialized(loadAccount);

token.on.inUse(() => router.push({ name: routenames.accounts.overview }));

const submit = handleSubmit(
  async (userDto) => {
    const createUpdateUserDto: CreateUpdateUserDto = {
      ...userDto,
      assignedRoles: userDto.assignedRoles,
      $type:
        CreateUpdateUserDtoTypeEnum.PtwPtw100AppZeroApplicationSharedAuthorizationUsersDtoCreateUpdateUserDtoPtwPtw100AppZeroApplicationShared,
    };

    const { success, result } = await api.usersApi.updateUser(
      {
        id: userDto.id,
        createUpdateUserDto,
      },
      [formBackendErrorHandler(form)],
    );

    if (success) {
      const accountStore = useAccountStore();
      if (accountStore?.currentUser?.id === result.id) {
        useAccountStore().currentUser = result;
      }
      await router.push({ name: routenames.accounts.overview });
    }
  },
  { withReferenceCheck: true },
);
</script>

<template>
  <form @submit="submit">
    <BasePage :loading="loading">
      <template #title>
        <BasePageTitle :title="fullName(values)" :titleId="values.iuid" />
      </template>

      <template #status>
        <BasePageStatus :entity="values" />
      </template>

      <template #actions>
        <PtwCancelButton :to="{ name: routenames.accounts.overview }" />
        <VeeSubmitButton class="ml-4" />
      </template>
      <AccountForm />
    </BasePage>
  </form>
</template>
